import type { Locale } from "./i18n";

/** Relative doc path without locale prefix (e.g. "introduction/overview"). */
export type DocPath =
  | "introduction/overview"
  | "introduction/about"
  | "introduction/getting-started"
  | "introduction/comparison"
  | "key-concepts/uai"
  | "key-concepts/point-of-sale"
  | "key-concepts/payment-request"
  | "key-concepts/settlements-and-consolidation"
  | "key-concepts/naspip/introduction"
  | "key-concepts/naspip/protocol-specification"
  | "key-concepts/naspip/security-model"
  | "key-concepts/naspip/implementation-guide"
  | "payment-flows/std-flow"
  | "payment-flows/edge-cases"
  | "merchant-integration/sign-up"
  | "merchant-integration/auth-and-conf"
  | "merchant-integration/create-payreq"
  | "developers/tutorials/api-integration-guide"
  | "developers/tutorials/payment-request"
  | "developers/tutorials/subscriptions"
  | "developers/subscriptions"
  | "developers/api"
  | "developers/sdk"
  | "developers/compatible-apps"
  | "developers/webhooks"
  | "developers/webhook-signature-verification"
  | "developers/transactions"
  | "developers/webhook-events"
  | "developers/api-credentials"
  | "developers/mcp"
  | "developers/payment-links"
  | "developers/two-factor-auth"
  | "developers/team-and-roles"
  | "developers/support-requests"
  | "developers/payment-notifications"
  | "applications/use-cases"
  | "faq"
  | "roadmap";

export const DOC_LABELS: Record<Locale, Record<DocPath, string>> = {
  en: {
    "introduction/overview": "Fluxis Payment Processor",
    "introduction/about": "What is Fluxis?",
    "introduction/getting-started": "Getting Started with Fluxis API",
    "introduction/comparison": "Fluxis API vs Self-Implementation",
    "key-concepts/uai": "Unique Asset IDs (UAI)",
    "key-concepts/point-of-sale": "Point of Sales",
    "key-concepts/payment-request": "Payment Requests",
    "key-concepts/settlements-and-consolidation": "Settlements & Consolidation",
    "key-concepts/naspip/introduction": "NASPIP Protocol",
    "key-concepts/naspip/protocol-specification": "Protocol Specification",
    "key-concepts/naspip/security-model": "Security Model",
    "key-concepts/naspip/implementation-guide": "Implementation Guide",
    "payment-flows/std-flow": "Payment Flow — Happy Path",
    "payment-flows/edge-cases": "Payment Flow — Edge Cases",
    "merchant-integration/sign-up": "Merchant Sign Up",
    "merchant-integration/auth-and-conf": "Your Dashboard & Default POS",
    "merchant-integration/create-payreq": "Accept Payments from the Dashboard",
    "developers/tutorials/api-integration-guide": "API Integration Guide",
    "developers/tutorials/payment-request": "Create and Monitor Payment Requests",
    "developers/tutorials/subscriptions": "Bill a Customer Every Month",
    "developers/subscriptions": "Subscriptions (alpha)",
    "developers/api": "Fluxis REST API",
    "developers/sdk": "SDKs",
    "developers/compatible-apps": "Compatible Apps Registry",
    "developers/webhooks": "Webhooks",
    "developers/webhook-signature-verification": "Webhook Signature Verification",
    "developers/transactions": "Transactions",
    "developers/webhook-events": "Webhook Events",
    "developers/api-credentials": "API Credentials",
    "developers/mcp": "Merchant MCP",
    "developers/payment-links": "Payment Links",
    "developers/two-factor-auth": "Two-Factor Authentication",
    "developers/team-and-roles": "Team & Roles",
    "developers/support-requests": "Support Requests",
    "developers/payment-notifications": "Payment Notifications",
    "applications/use-cases": "Use Cases",
    faq: "FAQ",
    roadmap: "Product Roadmap",
  },
  es: {
    "introduction/overview": "Procesador de pagos Fluxis",
    "introduction/about": "¿Qué es Fluxis?",
    "introduction/getting-started": "Primeros pasos con Fluxis API",
    "introduction/comparison": "Fluxis API vs implementación propia",
    "key-concepts/uai": "Identificadores únicos de activos (UAI)",
    "key-concepts/point-of-sale": "Puntos de venta",
    "key-concepts/payment-request": "Solicitudes de pago",
    "key-concepts/settlements-and-consolidation": "Liquidaciones y consolidación",
    "key-concepts/naspip/introduction": "Protocolo NASPIP",
    "key-concepts/naspip/protocol-specification": "Especificación del protocolo",
    "key-concepts/naspip/security-model": "Modelo de seguridad",
    "key-concepts/naspip/implementation-guide": "Guía de implementación",
    "payment-flows/std-flow": "Flujo de pago — Camino feliz",
    "payment-flows/edge-cases": "Flujo de pago — Casos límite",
    "merchant-integration/sign-up": "Registro de comercio",
    "merchant-integration/auth-and-conf": "Tu Dashboard y POS predeterminado",
    "merchant-integration/create-payreq": "Aceptar pagos desde el Dashboard",
    "developers/tutorials/api-integration-guide": "Guía de integración API",
    "developers/tutorials/payment-request": "Crear y monitorear solicitudes de pago",
    "developers/tutorials/subscriptions": "Cobrarle a un cliente todos los meses",
    "developers/subscriptions": "Suscripciones (alfa)",
    "developers/api": "API REST de Fluxis",
    "developers/sdk": "SDKs",
    "developers/compatible-apps": "Registro de apps compatibles",
    "developers/webhooks": "Webhooks",
    "developers/webhook-signature-verification": "Verificación de firma de webhook",
    "developers/transactions": "Transacciones",
    "developers/webhook-events": "Eventos de webhook",
    "developers/api-credentials": "Credenciales API",
    "developers/mcp": "MCP para comercios",
    "developers/payment-links": "Links de pago",
    "developers/two-factor-auth": "Autenticación de dos factores",
    "developers/team-and-roles": "Equipo y roles",
    "developers/support-requests": "Solicitudes de soporte",
    "developers/payment-notifications": "Notificaciones de pago",
    "applications/use-cases": "Casos de uso",
    faq: "Preguntas frecuentes",
    roadmap: "Hoja de ruta del producto",
  },
  pt: {
    "introduction/overview": "Processador de pagamentos Fluxis",
    "introduction/about": "O que é Fluxis?",
    "introduction/getting-started": "Primeiros passos com Fluxis API",
    "introduction/comparison": "Fluxis API vs implementação própria",
    "key-concepts/uai": "Identificadores únicos de ativos (UAI)",
    "key-concepts/point-of-sale": "Pontos de venda",
    "key-concepts/payment-request": "Solicitações de pagamento",
    "key-concepts/settlements-and-consolidation": "Liquidações e consolidação",
    "key-concepts/naspip/introduction": "Protocolo NASPIP",
    "key-concepts/naspip/protocol-specification": "Especificação do protocolo",
    "key-concepts/naspip/security-model": "Modelo de segurança",
    "key-concepts/naspip/implementation-guide": "Guia de implementação",
    "payment-flows/std-flow": "Fluxo de pagamento — Caminho feliz",
    "payment-flows/edge-cases": "Fluxo de pagamento — Casos extremos",
    "merchant-integration/sign-up": "Cadastro de comerciante",
    "merchant-integration/auth-and-conf": "Seu Dashboard e POS padrão",
    "merchant-integration/create-payreq": "Aceitar pagamentos pelo Dashboard",
    "developers/tutorials/api-integration-guide": "Guia de integração API",
    "developers/tutorials/payment-request": "Criar e monitorar solicitações de pagamento",
    "developers/tutorials/subscriptions": "Cobrar um cliente todo mês",
    "developers/subscriptions": "Assinaturas (alfa)",
    "developers/api": "API REST da Fluxis",
    "developers/sdk": "SDKs",
    "developers/compatible-apps": "Registro de apps compatíveis",
    "developers/webhooks": "Webhooks",
    "developers/webhook-signature-verification": "Verificação de assinatura de webhook",
    "developers/transactions": "Transações",
    "developers/webhook-events": "Eventos de webhook",
    "developers/api-credentials": "Credenciais de API",
    "developers/mcp": "MCP para comerciantes",
    "developers/payment-links": "Links de pagamento",
    "developers/two-factor-auth": "Autenticação de dois fatores",
    "developers/team-and-roles": "Equipe e pap\u00e9is",
    "developers/support-requests": "Solicitações de suporte",
    "developers/payment-notifications": "Notifica\u00e7\u00f5es de pagamento",
    "applications/use-cases": "Casos de uso",
    faq: "Perguntas frequentes",
    roadmap: "Roadmap do produto",
  },
};
